import React, { useEffect, useRef, useState } from 'react';
import { Button, DynamicForm } from 'components';
import { Props } from 'types/modals';
import Base from '../../base';
import useApi from 'hooks/useApi';
import endpoints from 'constants/endpoints';
import { getURLExtension } from 'utils/data';
import useFormHandler from 'hooks/useFormHandler';
import schema from './schema';
import { AlcoholCertificationsStatusTypes } from 'types/alcoholCertification';

const acceptedImgExtensions = ['png', 'jpg', 'jpeg'];

type FileItem = {
  url: string;
  type: string;
};

const AlcoholCertification = ({ closing, onClose, params }: Props) => {
  const api = useApi();
  const apiFile = useApi();
  const apiS3 = useApi();
  const apiSave = useApi();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const formHandler = useFormHandler(schema);
  const employeeId = String(params?.employeeId);

  useEffect(() => {
    api.fetchData(endpoints.GET_CERTIFICATION_URL.get({ employeeId }));
  }, []);

  useEffect(() => {
    if (api.response) {
      const certifications: any[] = api?.response?.alcoholCertification?.files || [];
      const items: FileItem[] = [];

      for (const item of certifications) {
        items.push({ url: item.url, type: getURLExtension(item.url)! });
      }

      setFiles(items);
    }
  }, [api.response]);

  useEffect(() => {
    if (params?.status === null) {
      params.status = AlcoholCertificationsStatusTypes.INCOMPLETE;
    }
    formHandler.setFullForm({ alcoholCertification: params.status });
  }, []);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = Array.from(e.target.files || []);
    setSelectedFiles(f);
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    try {
      setUploading(true);
      const uploadedPaths: string[] = [];

      for (const file of selectedFiles) {
        const signed = await apiFile.fetchData(endpoints.FILE_ALCOHOL_CERTIFICATION.get({ employeeId }), {
          fileName: file.name
        });
        if (!signed?.url || !signed?.extension || !signed?.filePath) continue;

        // Map jpg -> jpeg for content-type mapping
        const ext = signed.extension === 'jpg' ? 'jpeg' : signed.extension;
        await apiS3.uploadFileToS3(file, ext, signed.url);
        uploadedPaths.push(signed.filePath);
      }

      if (uploadedPaths.length) {
        await apiSave.fetchData(
          endpoints.SAVE_ALCOHOL_CERTIFICATION.get({ employeeId }),
          { filePaths: uploadedPaths },
          true
        );
        // Refresh viewer
        await api.fetchData(endpoints.GET_CERTIFICATION_URL.get({ employeeId }));
        setSelectedFiles([]);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Base
      header="Alcohol Certification"
      closing={closing}
      onClose={onClose}
      size="lg"
      footer={
        <Button
          text="Save changes"
          onClick={() => onClose({ employeeId, status: formHandler.data.alcoholCertification })}
          disabled={!formHandler.valid}
        />
      }
    >
      <div className="alcohol-viewer-container">
        <div className="upload-controls">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            multiple
            onChange={onFileChange}
            style={{ display: 'none' }}
          />
          <Button text="Select files" onClick={() => fileInputRef.current?.click()} disabled={uploading} />
          <Button
            text={uploading ? 'Uploading…' : 'Upload'}
            onClick={handleUpload}
            disabled={uploading || selectedFiles.length === 0}
          />
          {selectedFiles.length > 0 && (
            <div className="file-list" aria-live="polite">
              {selectedFiles.map((f, i) => (
                <span key={i} className="file-chip" title={f.name}>
                  {f.name}
                </span>
              ))}
            </div>
          )}
        </div>
        {files?.map((file, index) =>
          file.type === 'pdf' ? (
            <div key={index} className="viewer-pdf">
              <iframe src={file.url} title="Alcohol Certificate" height="500px" />
            </div>
          ) : acceptedImgExtensions.includes(file.type) ? (
            <div key={index} className="viewer-img">
              <img src={file.url} className="image" />
            </div>
          ) : null
        )}
        <div className="form-wrapper">
          <DynamicForm handler={formHandler} />
        </div>
      </div>
    </Base>
  );
};

export default AlcoholCertification;
